/**
 * Supported locale configuration for the provider-facing app.
 *
 * Locale tags follow BCP 47. `en` is the default and fallback locale; if a key
 * is missing in a non-English locale, i18next falls back to English.
 *
 * This module owns all locale detection, normalization and persistence so the
 * mapping between browser languages (e.g. `es`, `pt`, `fr-CA`) and the four
 * supported app locales lives in exactly one place.
 */

export const FALLBACK_LOCALE = 'en';

/** localStorage key used to persist the provider's explicit language choice. */
export const LANGUAGE_STORAGE_KEY = 'donvio_language';

export interface SupportedLocale {
  /** BCP 47 tag used as the i18next language code. */
  code: string;
  /** Native-language label shown in selectors (e.g. "Español"). */
  label: string;
  /** English label, used for accessibility/debug contexts. */
  englishLabel: string;
  /** Locale string passed to Luxon and MUI date pickers / Intl formatters. */
  intlLocale: string;
}

export const SUPPORTED_LOCALES: readonly SupportedLocale[] = [
  { code: 'en', label: 'English', englishLabel: 'English', intlLocale: 'en-US' },
  { code: 'es-US', label: 'Español', englishLabel: 'Spanish', intlLocale: 'es-US' },
  { code: 'pt-BR', label: 'Português', englishLabel: 'Portuguese', intlLocale: 'pt-BR' },
  { code: 'fr', label: 'Français', englishLabel: 'French', intlLocale: 'fr-FR' },
] as const;

export const SUPPORTED_LOCALE_CODES: readonly string[] = SUPPORTED_LOCALES.map(
  locale => locale.code,
);

export const isSupportedLocale = (code: string | null | undefined): boolean =>
  !!code && SUPPORTED_LOCALE_CODES.includes(code);

/**
 * Map an arbitrary language tag onto a supported app locale.
 *
 * Resolution order:
 *  1. Exact match (case-insensitive) on a supported code.
 *  2. Base-language match: `es` -> `es-US`, `pt` -> `pt-BR`, `fr-CA` -> `fr`,
 *     `en-GB` -> `en`.
 * Returns `null` when nothing matches so callers can fall through to English.
 */
export const normalizeLocale = (raw?: string | null): string | null => {
  if (!raw) return null;
  const lower = raw.toLowerCase();

  const exact = SUPPORTED_LOCALES.find(
    locale => locale.code.toLowerCase() === lower,
  );
  if (exact) return exact.code;

  const base = lower.split('-')[0];
  const baseMatch = SUPPORTED_LOCALES.find(
    locale => locale.code.toLowerCase().split('-')[0] === base,
  );
  return baseMatch ? baseMatch.code : null;
};

export const getStoredLanguage = (): string | null => {
  try {
    return normalizeLocale(localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch {
    return null;
  }
};

export const storeLanguage = (code: string): void => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
  } catch {
    // localStorage may be unavailable (private mode / SSR) — non-fatal.
  }
};

/** Best supported locale derived from the browser, or English fallback. */
export const detectBrowserLanguage = (): string => {
  const candidates =
    typeof navigator !== 'undefined'
      ? navigator.languages ?? [navigator.language]
      : [];
  for (const candidate of candidates) {
    const normalized = normalizeLocale(candidate);
    if (normalized) return normalized;
  }
  return FALLBACK_LOCALE;
};

/**
 * Resolve the language to boot with: an explicit saved choice wins; otherwise
 * fall back to browser detection (first-time default only), then English.
 */
export const resolveInitialLanguage = (): string =>
  getStoredLanguage() ?? detectBrowserLanguage();

/** Locale string for Luxon / MUI date pickers / Intl given an app locale. */
export const getIntlLocale = (code: string): string =>
  SUPPORTED_LOCALES.find(locale => locale.code === code)?.intlLocale ?? 'en-US';
